import React, {useEffect} from "react";
import {Box} from "@mui/system";
import {Checkbox, FormControl, MenuItem, Select, Typography} from "@mui/material";
import {useStrategiesContext} from "../../../context/derivatives/strategies/DerivativesStrategiesContext";
import FormControlLabel from "@mui/material/FormControlLabel";
import {useSearchParams} from "react-router-dom";
import useResponsive from "../../../components/hooks/useResponsive";
import {COLORS} from "../../../ui/Theme";
import {AppRegistration, SwapVert} from '@mui/icons-material';

const DerivativesStrategiesForm = () => {
    const {
        strategies,
        setSelectedRowDetails,
        selectedRiskLevel,
        selectedStrategy,
        setSelectedRiskLevel,
        setSelectedStrategy,
        isMoreThanOneWeekChecked,
        setIsMoreThanOneWeekChecked,
        isRemoveAppliedPriceChecked,
        setIsRemoveAppliedPriceChecked,
    } = useStrategiesContext();

    const {isSm} = useResponsive();
    const [searchParams, setSearchParams] = useSearchParams();

    const currentStrategies = strategies.find(
        (strategy) => strategy.key === selectedRiskLevel?.key
    )?.data?.data || [];

    useEffect(() => {
        const riskLevelKey = searchParams.get("risk_level");
        const strategyKey = searchParams.get("strategy");

        if (riskLevelKey && strategies.length > 0) {
            const riskLevel = strategies.find((strategy) => strategy.key === riskLevelKey);
            setSelectedRiskLevel(riskLevel || null);
        }

        if (strategyKey && selectedRiskLevel) {
            const strategy = currentStrategies.find((strat) => strat.key === strategyKey);
            setSelectedStrategy(strategy || null);
        }
    }, [searchParams, currentStrategies]);

    const handleRiskLevelChange = (e) => {
        const riskLevel = strategies.find((start) => start.key === e.target.value);
        setSelectedRiskLevel(riskLevel || null);

        if (riskLevel) {
            searchParams.delete("strategy");
            setSelectedStrategy([])
            setSelectedRowDetails({
                actions: [],
                coordinates: [],
                profit_factor: null,
                base_equity_last_price: null,
            })
            setSearchParams({
                risk_level: e.target.value,
            });
        }
    };

    const handleStrategyChange = (e) => {
        const strategy = currentStrategies.find((strat) => strat.key === e.target.value);
        setSelectedStrategy(strategy || null);

        if (selectedRiskLevel && strategy) {
            setSelectedRowDetails({
                actions: [],
                coordinates: [],
                profit_factor: null,
                base_equity_last_price: null,
            })
            setSearchParams({
                risk_level: selectedRiskLevel.key,
                strategy: e.target.value,
            });
        }
    };

    return (
        <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: {xs: "column", md: "row"},
            gap: "20px"
        }}>
            {/*DROPDOWNS*/}
            <Box sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: isSm ? "row" : "column  ",
                gap: "10px",
            }}
            >
                {/*RISK LEVEL*/}
                <FormControl sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: {xs: "5px", lg: "10px"},
                    width: {xs: "100%", sm: "300px"},
                }}>
                    <Typography
                        sx={{
                            display: {xs: "none", sm: "block"},
                            color: COLORS.GRAY,
                            fontSize: "12px",
                            fontWeight: 400,
                            padding: "0 10px",
                        }}
                    >
                        نوع
                    </Typography>
                    <Select
                        sx={{width: {xs: "100%", lg: "200px"}}}
                        labelId="riskLevel"
                        id="riskLevel"
                        value={selectedRiskLevel?.key || ""}
                        onChange={handleRiskLevelChange}
                        startAdornment={
                            <SwapVert
                                sx={{
                                    display: {xs: "block", sm: "none"},
                                    marginRight: "8px",
                                    color: "#9E9E9E",
                                }}
                            />
                        }
                    >
                        {strategies.map((strategy) => (
                            <MenuItem key={strategy.key} value={strategy.key}>
                                {strategy.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/*STRATEGY*/}
                <FormControl
                    sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: {xs: "5px", lg: "10px"},
                        width: {xs: "100%", sm: "300px"},
                    }}>
                    <Typography
                        sx={{
                            display: {xs: "none", sm: "block"},
                            color: COLORS.GRAY,
                            fontSize: "12px",
                            fontWeight: 400,
                            padding: "0 10px",
                        }}
                    >
                        استراتژی
                    </Typography>
                    <Select
                        sx={{width: {xs: "100%", lg: "200px"}}}
                        labelId="strategy"
                        id="strategy"
                        value={selectedStrategy?.key || ""}
                        onChange={handleStrategyChange}
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) {
                                return <Typography sx={{fontSize: "12px", color: "#9E9E9E"}}>انتخاب
                                    استراتژی</Typography>;
                            }
                            return currentStrategies.find((strategy) => strategy.key === selected)?.name || "";
                        }}
                        startAdornment={
                            <AppRegistration
                                sx={{
                                    display: {xs: "block", sm: "none"},
                                    fontSize: "18px",
                                    marginRight: "8px",
                                    color: "#9E9E9E",
                                }}
                            />
                        }
                    >
                        {currentStrategies.map((strategy) => (
                            <MenuItem key={strategy.key} value={strategy.key}>
                                {strategy.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/*CHECK BOXES*/}
            <Box sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: {xs: "column", md: "row"},
                gap: "16px",
            }}>
                <Box sx={{
                    display: "flex",
                    width: {xs: "100%", md: "fit-content"},
                    gap: "16px",
                    margin: "0",
                    padding: "8px 10px",
                    fontWeight: 400,
                    color: COLORS.BLACK,
                    backgroundColor: COLORS.INPUTBG,
                    borderRadius: "10px",
                }}>
                    <FormControlLabel
                        sx={{
                            gap: "8px",
                            margin: "0",
                        }}
                        label={
                            <Typography sx={{fontSize: "14px", fontWeight: 400, color: COLORS.BLACK}}>
                                بیشتر از یک هفته تا اعمال
                            </Typography>
                        }
                        control={
                            <Checkbox
                                sx={{padding: "0"}}
                                checked={isMoreThanOneWeekChecked}
                                onChange={() => setIsMoreThanOneWeekChecked((prev) => !prev)}
                            />
                        }
                    />
                </Box>
                <Box sx={{
                    display: "flex",
                    width: {xs: "100%", md: "fit-content"},
                    gap: "16px",
                    margin: "0",
                    backgroundColor: COLORS.INPUTBG,
                    borderRadius: "10px",
                    padding: "8px 10px",
                    fontWeight: 400,
                    color: COLORS.BLACK,
                }}>
                    <FormControlLabel
                        sx={{
                            gap: "8px",
                            margin: "0",
                        }}
                        label={
                            <Typography sx={{fontSize: "14px", fontWeight: 400, color: COLORS.BLACK}}>
                                حذف قیمت اعمال بالا
                            </Typography>
                        }
                        control={
                            <Checkbox
                                sx={{padding: "0"}}
                                checked={isRemoveAppliedPriceChecked}
                                onChange={() => setIsRemoveAppliedPriceChecked((prev) => !prev)}
                            />
                        }
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default DerivativesStrategiesForm;